/**
 * Prefill bridge into the purchase wizard (WI-TV-W6-001).
 *
 * Purchase keeps exactly the trust boundaries refinance has: this module never
 * listens for messages and never decides what is trusted. Values arrive here
 * only after `handlePrefillMessage` accepted the sender's exact allowlisted
 * origin, and they are then sanitized with the authoritative rules — anything
 * that is not an enum value, a valid state code, a five-digit ZIP, a ten-digit
 * phone, or a valid email is dropped rather than seeded. Seeded values stay
 * editable, the wizard still starts on step 1, and nothing is auto-advanced,
 * auto-skipped, or auto-submitted. No value is ever logged.
 *
 * URL query support for purchase is routing-only: `loanPurpose` selects the
 * journey and no other query parameter seeds purchase data. Exact-origin
 * postMessage is the only purchase data-prefill channel.
 *
 * The purchase finance seeds follow the locked WI-TV-W6-002 truth table. Wave 6
 * adds no partner-facing down-payment key: the visible purchase price and down
 * payment are derived from the existing `estimatedValue` and `loanAmount`
 * fields, each sanitized independently by the authoritative lossless money
 * helper. A loan amount that is missing, invalid, or not strictly within the
 * price seeds no down payment at all, so an invisible requested-loan value is
 * never preserved and a negative down payment is never invented. The
 * refinance-only goal and current-balance answers are still dropped.
 *
 * The manual property address is admissible only alongside the found-property
 * home-search answer, so it is dropped unless that answer is already recorded.
 * Otherwise a partner value would sit on an application whose borrower is still
 * looking, invisible because the expansion is not rendered and therefore
 * impossible to review or correct — the same cross-field rule the refinance
 * sanitizer applies to the military component.
 *
 * The optional co-borrower answers (WI-TV-W7-001) are journey-neutral: purchase
 * accepts exactly the secondary answers refinance does, under the same
 * namespaced keys and the same shared sanitizer, and drops none of them here.
 * They are not purchase-only transient state, so they also cross the trusted
 * purchase-to-refinance route with the rest of the borrower's shared answers.
 */
import {
  CURRENT_LOAN_BALANCE_KEY,
  ESTIMATED_VALUE_KEY,
  LOAN_AMOUNT_KEY,
  REFINANCE_TYPE_KEY,
} from "./refinance-steps";
import { readRefinancePrefillFromMapped, sanitizeRefinancePrefill } from "./refinance-prefill";
import {
  DOWN_PAYMENT_KEY,
  HOME_SEARCH_FOUND,
  HOME_SEARCH_STATUS_KEY,
  MANUAL_ADDRESS_KEYS,
} from "./purchase-steps";
import { moneyTextToCents, centsToMoneyText, subtractMoneyText } from "./money";

/** Keys that are meaningful to refinance only. */
const NON_PURCHASE_KEYS: readonly string[] = [REFINANCE_TYPE_KEY, CURRENT_LOAN_BALANCE_KEY];

function dropNonPurchaseKeys(seeded: Record<string, string>): Record<string, string> {
  for (const key of NON_PURCHASE_KEYS) delete seeded[key];
  // The down payment is a derived wizard answer, never a partner contract: an
  // unknown `downPayment` key stays ignored however it arrives.
  delete seeded[DOWN_PAYMENT_KEY];
  return seeded;
}

/**
 * Derives the visible purchase-price and down-payment seeds from the already
 * sanitized `estimatedValue` / `loanAmount` pair, per the locked truth table.
 *
 * Both partner values are canonical positive money by the time they get here
 * (the shared sanitizer drops anything else), so the only remaining question is
 * whether they are consistent with each other. A loan amount equal to the price
 * seeds a zero down payment, which is valid; one above the price is an
 * inconsistent pair whose loan value is dropped rather than turned into a
 * negative down payment or kept as an invisible requested loan; and a price that
 * was not accepted drops both, because no purchase state can be derived from a
 * loan amount alone.
 *
 * A message can arrive after the borrower has already answered, so a seeded
 * price that carries no usable loan amount is reconciled against the answers
 * they have: the derived loan amount is recomputed from the new price, never
 * left at the superseded price's result.
 */
function derivePurchaseFinanceSeeds(
  seeded: Record<string, string>,
  current: Readonly<Record<string, string>>,
): Record<string, string> {
  const price = seeded[ESTIMATED_VALUE_KEY];
  const loanAmount = seeded[LOAN_AMOUNT_KEY];
  delete seeded[LOAN_AMOUNT_KEY];

  if (price === undefined) {
    delete seeded[ESTIMATED_VALUE_KEY];
    return seeded;
  }

  if (loanAmount !== undefined) {
    const priceCents = moneyTextToCents(price);
    const loanCents = moneyTextToCents(loanAmount);
    const downPayment =
      priceCents !== null && loanCents !== null && loanCents <= priceCents
        ? centsToMoneyText(priceCents - loanCents)
        : null;
    if (downPayment !== null) {
      seeded[DOWN_PAYMENT_KEY] = downPayment;
      seeded[LOAN_AMOUNT_KEY] = loanAmount;
      return seeded;
    }
  }

  return reDeriveFromCurrentDownPayment(seeded, current, price);
}

/**
 * Re-derives the loan amount for a seeded price from the down payment the
 * borrower already entered.
 *
 * The borrower's own answer is never overwritten here, but the amount derived
 * from it is: a price that arrives while a later step is showing would otherwise
 * leave an estimated loan amount that no longer matches — possibly one above the
 * new price — on an application whose two money steps are not being rendered. A
 * pair that no longer derives a positive amount clears the derived value, so the
 * required down-payment step blocks on it instead.
 */
function reDeriveFromCurrentDownPayment(
  seeded: Record<string, string>,
  current: Readonly<Record<string, string>>,
  price: string,
): Record<string, string> {
  const currentDownPayment = (current[DOWN_PAYMENT_KEY] ?? "").trim();
  if (currentDownPayment === "") {
    if ((current[LOAN_AMOUNT_KEY] ?? "") !== "") seeded[LOAN_AMOUNT_KEY] = "";
    return seeded;
  }
  seeded[LOAN_AMOUNT_KEY] = subtractMoneyText(price, currentDownPayment) ?? "";
  return seeded;
}

/**
 * Drops the manual address unless the borrower has already said they found a
 * property, so nothing the home-search step keeps hidden can be seeded.
 */
function dropHiddenAddressKeys(
  seeded: Record<string, string>,
  current: Readonly<Record<string, string>>,
): Record<string, string> {
  const status = (seeded[HOME_SEARCH_STATUS_KEY] ?? current[HOME_SEARCH_STATUS_KEY] ?? "").trim();
  if (status === HOME_SEARCH_FOUND) return seeded;
  for (const key of MANUAL_ADDRESS_KEYS) delete seeded[key];
  return seeded;
}

/**
 * Sanitizes the mapped values of an accepted prefill message for the purchase
 * wizard. `mapped` is the output of `mapPrefillPayload` for a message whose
 * exact allowlisted origin was already accepted, and `current` is the wizard's
 * answers so far, which decide whether the manual address is admissible.
 */
export function readPurchasePrefillFromMapped(
  mapped: Readonly<Record<string, string>>,
  current: Readonly<Record<string, string>> = {},
): Record<string, string> {
  return dropHiddenAddressKeys(
    derivePurchaseFinanceSeeds(
      dropNonPurchaseKeys(readRefinancePrefillFromMapped(mapped)),
      current,
    ),
    current,
  );
}

/**
 * Sanitizes a snapshot of the mounted purchase journey's current in-memory
 * values into the refinance-compatible answers that may be retained when a
 * partner-routed prefill switches a switchable pre-submit purchase journey to
 * refinance (WI-TV-W6-005).
 *
 * The snapshot is keyed by the shared wizard value keys, so it is sanitized with
 * the authoritative refinance rules exactly like any other prefill: shared
 * property/finance/credit/military/contact answers survive when valid, while
 * purchase-only transient state — the derived down payment, the home-search
 * status, and any refinance-only key — is not a refinance prefill key and is
 * dropped, and a stale military component is dropped unless the answer is
 * affirmative. Sanitized co-borrower answers and the opt-in that carries them
 * are shared answers, so they are retained across the route; only the incoming
 * message's own accepted values may override them, and only the destructive
 * reset removes them. A manual property address the borrower cannot currently see,
 * entered before switching back to "still looking", is dropped first so a hidden
 * address can never cross the route.
 */
export function readRetainedRefinancePrefillFromPurchaseSnapshot(
  snapshot: Readonly<Record<string, string>>,
): Record<string, string> {
  const visible: Record<string, string> = { ...snapshot };
  const status = (snapshot[HOME_SEARCH_STATUS_KEY] ?? "").trim();
  if (status !== HOME_SEARCH_FOUND) {
    for (const key of MANUAL_ADDRESS_KEYS) delete visible[key];
  }
  return sanitizeRefinancePrefill(visible);
}

/** Sanitizes already-keyed wizard values for the purchase wizard. */
export function sanitizePurchasePrefill(
  raw: Readonly<Record<string, string | undefined>>,
  current: Readonly<Record<string, string>> = {},
): Record<string, string> {
  return dropHiddenAddressKeys(
    derivePurchaseFinanceSeeds(dropNonPurchaseKeys(sanitizeRefinancePrefill(raw)), current),
    current,
  );
}
